import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';
import { authService } from '@/shared/services/authService';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const queryClient = useQueryClient();
  const { session, user, isLoading, setSession, setUser, setLoading } = useAuthStore();

  useEffect(() => {
    // Check for existing session on mount
    authService
      .getSession()
      .then(async (session) => {
        setSession(session);
        if (session?.user) {
          // Only fetch user profile if user is missing from store
          // (user might already be cached from persisted state)
          if (!user) {
            const userProfile = await authService.getUserProfile(session.user.id);
            setUser(userProfile);
          } else if (user.id !== session.user.id) {
            // Different user logged in - clear all cache and fetch new profile
            queryClient.clear();
            const userProfile = await authService.getUserProfile(session.user.id);
            setUser(userProfile);
          }
          // If user exists and matches session, skip fetch (optimization)
          // Note: Permission changes will be detected on next page load or manual refresh
        }
      })
      .catch((error) => {
        console.error('Session check failed:', error);
        setSession(null);
        setUser(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [setSession, setUser, setLoading, user, queryClient]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (!session || !user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}
